/**
 * T1250 · SDK devtool event bus · `sdk.events` scaffold + DCE guard.
 *
 * Spec anchor: file 05 §devtool-events.
 *
 * **Five primary channels + system channels.**
 *
 *   Primary (devtool panels 1–5):
 *     - `cache:miss`            — fired when a hook resolves cold/stale/etc.
 *     - `cache:write`           — every `qc.setQueryData` mirror
 *     - `subscription:change`   — SUB/UNSUB on a NATS subject
 *     - `broadcast:message`     — cross-tab BroadcastChannel verdict
 *     - `mutation:phase`        — onMutate / fetch / success / settled
 *
 *   System (always live):
 *     - `applyEvent:error`      — patch-updater throw (T18001)
 *     - `onSignal:error`        — onSignal-updater throw (T17130)
 *     - `observer:quarantined`  — emitted once after 5 throws in 60 s
 *     - `error`                 — SdkError
 *     - `recovery`              — paired post-error settlement
 *     - `blockmeta:status`      — streamed confirmed blockmeta feed readiness/errors
 *     - `connectivity:change`   — ConnTransition
 *
 * **DCE guard.** Production builds skip every `emit()` call when
 *
 *     process.env.NODE_ENV === 'production' &&
 *     globalThis.__MORFI_DEVTOOL_ENABLED__ !== true
 *
 * In that mode the call is a single boolean read that minifiers / bundlers
 * trivially fold to a no-op. The `__MORFI_DEVTOOL_ENABLED__` global lets
 * QA enable the bus on a prod build without recompiling.
 *
 * The two error channels (`applyEvent:error` + `onSignal:error`) are
 * STRUCTURALLY DISTINCT — emitting one does not fan out to the other.
 * Devtool panel 5 surfaces both side-by-side.
 */

import type { ConnTransition } from '../connectivity/fsm/state-machine.js';

declare const process: { env: { NODE_ENV?: string } };

type EventListener = (payload: unknown) => void;

/**
 * Public structural shape of the SDK event bus. Re-exporters
 * (`conditional-export.ts`, etc.) annotate their re-export with this
 * interface so TypeScript does not leak the implementation class's
 * private members through the inferred type.
 */
export interface ISdkEventBus {
  on(channel: string, listener: EventListener): () => void;
  emit(channel: string, payload: unknown): void;
  reset(): void;
}

class SdkEventBus implements ISdkEventBus {
  private readonly listeners = new Map<string, Set<EventListener>>();

  on(channel: string, listener: EventListener): () => void {
    let set = this.listeners.get(channel);
    if (set === undefined) {
      set = new Set();
      this.listeners.set(channel, set);
    }
    set.add(listener);
    return (): void => {
      set?.delete(listener);
    };
  }

  emit(channel: string, payload: unknown): void {
    const set = this.listeners.get(channel);
    if (set === undefined) return;
    for (const listener of set.values()) {
      listener(payload);
    }
  }

  /** Test-only — drop every registered listener. */
  reset(): void {
    this.listeners.clear();
  }
}

export const sdkEvents: ISdkEventBus = new SdkEventBus();

/**
 * Production DCE guard. Returns true when the devtool bus should be
 * live. In production this is a single property-read against the global
 * `__MORFI_DEVTOOL_ENABLED__` flag — minifiers fold the `if (false) {}`
 * branch out of every emit call site.
 */
export function isDevtoolEnabled(): boolean {
  const isProd = typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
  if (!isProd) return true;
  const g = globalThis as { __MORFI_DEVTOOL_ENABLED__?: boolean };
  return g.__MORFI_DEVTOOL_ENABLED__ === true;
}

/**
 * Canonical `emit` entry point — apply the DCE guard once, then dispatch.
 * Callers should prefer this over `sdkEvents.emit` so the dead-code branch
 * lands in the production bundle as a single boolean check.
 */
export function emit(channel: string, payload: unknown): void {
  if (!isDevtoolEnabled()) return;
  sdkEvents.emit(channel, payload);
}

/**
 * List of canonical PRIMARY devtool-panel channels (panels 1–5).
 * Pinned at 5 — `subscription:never-bound` (T18800) is typed on the
 * `SdkEvents` map but lives outside this list because it is a SYSTEM
 * channel (dev-only viewport gating guard), not a primary panel.
 */
export const SDK_EVENT_CHANNELS = [
  'cache:miss',
  'cache:write',
  'subscription:change',
  'broadcast:message',
  'mutation:phase',
] as const;

/** All system channels — error + recovery + persister + connectivity + churn + never-bound + reacquire + realtime. */
export const SDK_SYSTEM_CHANNELS = [
  'polling:policy',
  'applyEvent:error',
  'onSignal:error',
  'observer:quarantined',
  'error',
  'recovery',
  'blockmeta:status',
  'connectivity:change',
  'churn:subscript-thrash',
  'subscription:never-bound',
  'subject:reacquire',
  'realtime:dispatch',
  'viewer-overlay:diagnostic',
  'public-user:diagnostic',
  'backer-reward:diagnostic',
  'conviction-projector:diagnostic',
  'resolution-threshold:diagnostic',
  'cache:diagnostic',
  'outcome-entity:diagnostic',
  'trade-entity:diagnostic',
  'position-entity:diagnostic',
  'participation-entity:diagnostic',
  'realtime:handoff-recovery',
] as const;

/**
 * `realtime:dispatch` event shape — emitted by `useRealtimeQuery` every
 * time a NATS event is dispatched into the cache. Surfaces:
 *   - which subject pattern fired,
 *   - the spec + queryKey it routed to,
 *   - which dispatch branch ran (`applyEvent` / `onSignal` / `pure-invalidate`),
 *   - how long the reducer took (ms),
 *   - whether the dispatcher threw.
 *
 * Used by `useSdkRealtimeDispatch()` to power the realtime-dispatch
 * timeline in the devtool. NOT a routine `cache:write` channel event —
 * cache:write fires AFTER the reducer when `applyEvent` succeeds, but
 * realtime:dispatch fires UNCONDITIONALLY (including invalidate and
 * no-op signalFn branches that produce no cache:write).
 */
export interface RealtimeDispatchEvent {
  /** specName from the HookSpec (`useGetMarket`, etc.). */
  readonly specName: string;
  /** The subject pattern that fired (e.g. `morfi.market.X.trade`). */
  readonly subject: string;
  /** Concrete queryKey the dispatch targeted. */
  readonly queryKey: readonly unknown[];
  /** TanStack hash of the queryKey. */
  readonly queryHash: string;
  /** Which dispatch branch ran. */
  readonly branch: 'applyEvent' | 'onSignal:invalidate' | 'onSignal:noop' | 'invalidate';
  /** Reducer duration in ms (always ≥ 0; sub-ms = 0). */
  readonly durationMs: number;
  /** `null` on success; error message string on throw. */
  readonly error: string | null;
}

/**
 * T16701 · `churn:subscript-thrash` event shape — emitted when the
 * SubjectRegistry detects rapid sub/unsub cycles for a single
 * specName+queryKeyHash within a short dwell window. Drives the
 * thrash-detector devtool panel + Better Stack escalation.
 */
export interface ChurnThrashEvent {
  readonly specName: string;
  readonly queryKeyHash: string;
  readonly dwellMs: number;
  readonly observedAt: number;
}

/**
 * T18800 · `subscription:never-bound` event shape — emitted by
 * `useRealtimeQuery` (T2003) when a `viewportGated` hook never attaches
 * its callback ref to a DOM element within the dev/prod timeout window.
 *
 * Spec 05 §L362–398 / §L386. The dev variant fires after 5 s; the prod
 * variant after 30 s and is rate-limited at 1% sample (`sdk.viewport_unbound`
 * carries the prod signal through the separate Better Stack pathway).
 *
 * Promoted into the typed `SdkEvents` map by T18800 so consumers get
 * compile-time payload validation; the previous untyped string emit
 * bypassed the T15704 union drift guard.
 */
export interface SubscriptionNeverBoundEvent {
  readonly specName: string;
  readonly queryKey: readonly unknown[];
}

/**
 * Public `SdkEvents` type union — canonical channel name → payload
 * mapping. Importers from `core/events` see the SAME type via the
 * re-export bridge (T15700 singleton invariant). Adding a new channel
 * here is a coordinated edit (see T16701 acceptance: enumeration tests
 * fail to compile until both ends agree).
 */
/**
 * `subject:reacquire` event payload — fired by `SubjectRegistry` when a
 * per-entity subject's `interestCount` transitions 0→1 after a gap
 * exceeding `GRACE_MS` (i.e. the wire SUB was torn down between
 * subscriptions). The hook layer's default impl refetches the query;
 * consumers can override via `RealtimeSpec.onReacquire`.
 */
export interface SubjectReacquireEvent {
  /** Concrete subject re-acquired (e.g. `morfi.market.M1.trade`). */
  readonly subject: string;
  /** Last seq the registry saw on this subject; 0 if never any. */
  readonly lastSeq: number;
  /** Wall-clock gap duration, ms. Always > GRACE_MS. */
  readonly gapMs: number;
}

export interface BlockmetaStatusEvent {
  readonly level: 'info' | 'warn';
  readonly message: string;
  readonly subject: string;
  readonly mode?: string;
  readonly reason?: string;
  readonly err?: string;
}

/** Failure-only signal for a snapshot/wire handoff that must refetch. */
export interface RealtimeHandoffRecoveryEvent {
  readonly specName: string;
  readonly reason:
    | 'not_ready'
    | 'missing_cursor'
    | 'malformed_cursor'
    | 'buffer_overflow'
    | 'consumer_recreated'
    | 'replay_unavailable'
    | 'deadline_forced_open';
}

export interface SdkEvents {
  readonly 'polling:policy': {
    readonly operationName: string;
    readonly mode: string;
    readonly intervalMs: number | false;
    readonly reason: string;
  };
  readonly 'cache:miss': unknown;
  readonly 'cache:write': unknown;
  readonly 'subscription:change': unknown;
  readonly 'subscription:never-bound': SubscriptionNeverBoundEvent;
  readonly 'broadcast:message': unknown;
  readonly 'mutation:phase': unknown;
  readonly 'applyEvent:error': unknown;
  readonly 'onSignal:error': unknown;
  readonly 'observer:quarantined': unknown;
  readonly error: unknown;
  readonly recovery: unknown;
  readonly 'blockmeta:status': BlockmetaStatusEvent;
  readonly 'connectivity:change': ConnTransition;
  readonly 'churn:subscript-thrash': ChurnThrashEvent;
  readonly 'subject:reacquire': SubjectReacquireEvent;
  readonly 'realtime:dispatch': RealtimeDispatchEvent;
  readonly 'viewer-overlay:diagnostic': unknown;
  readonly 'public-user:diagnostic': unknown;
  readonly 'backer-reward:diagnostic': unknown;
  readonly 'conviction-projector:diagnostic': unknown;
  readonly 'resolution-threshold:diagnostic': {
    readonly entityHash: string;
    readonly reason: string;
  };
  readonly 'cache:diagnostic': {
    readonly kind: 'market-config' | 'persistence';
    readonly reason: string;
    readonly count?: number;
  };
  readonly 'outcome-entity:diagnostic': unknown;
  readonly 'trade-entity:diagnostic': unknown;
  readonly 'position-entity:diagnostic': unknown;
  readonly 'participation-entity:diagnostic': unknown;
  readonly 'realtime:handoff-recovery': RealtimeHandoffRecoveryEvent;
}
